import { App } from 'antd';
import { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FileUploadStatusEnum } from '../../enums/FileUploadStatusEnum';
import { QuickShareStatusEnum } from '../../enums/QuickShareStatusEnum';
import { SpecialFolderEnum } from '../../enums/SpecialFolderEnum';
import type GroupedUploadItem from '../../interfaces/GroupedUploadItem';
import type UploadFile from '../../interfaces/UploadFile';
import { FileService } from '../../services/fileApiService';
import { generateUUID } from '../../utils/utils';
import { useAuth } from '../useAuth';
import { useFileUpload } from '../useFileUpload';

const fileService = new FileService();

function useQuickShare() {
  const { notification } = App.useApp();
  const { t } = useTranslation();
  const { isExternal, isReader, authenticatedUser, redirectLoggedInUser } = useAuth();
  const location = useLocation();

  // Redirect away from Quick Share only when user is on the Quick Share route and does not have access (isQSAvailable === false).

  const {
    isUploadingQuickShareFiles,
    uploadFiles,
    getFileTree,
    groupUploadItems,
    getFilesToEnqueue,
    buildFileTreeFromUploadFiles,
    uploadQuickShareGroupedFilesQueue,
    uploadQuickShareGroupedFilesQueueWithoutRootFolder,
    emptyQuickShareUploadFilesQueue,
    handleQuickShareInputChange,
    handleShareQuickShareFiles,
    quickShareForm,
    failedUploadQuickShareFiles,
    totalQuickShareGroupedFilesAmount,
    uploadedQuickShareGroupedFilesAmount,
    retryFailedUploads: retryFailedUploadsFromContext,
    quickShareGlobalProgressByBytes,
    isNetworkAvailable,
  } = useFileUpload();
  const [filesQueue, setFilesQueue] = useState<UploadFile[]>([]);
  const [groupedFilesQueue, setGroupedFilesQueue] = useState<GroupedUploadItem[]>([]);
  const [isPageLoaded, setIsPageLoaded] = useState(false);

  /**
   * Represents the current status of the file upload process in the Quick Share feature.
   *
   * The status determined depends on various conditions:
   * - If there is no internet connection, the status is `ConnectionError`.
   * - If the upload file queue is empty, the status is `Pending`.
   * - If all files in the queue have completed uploading, the status is `Completed`.
   * - If all files in the queue have failed to upload, the status is `Error`.
   * - If at least one file has failed but not all, the status is `SomeErrors`.
   * - If the files are in the process of uploading and none of the above conditions are met, the status is `Uploading`.
   *
   * Dependencies:
   * - `isOnline`: Determines if there is an active internet connection.
   * - `uploadQuickShareGroupedFilesQueue`: Tracks the files and their individual upload statuses.
   *
   * Uses:
   * - `QuickShareStatusEnum` to specify the current upload state.
   * - `FileUploadStatusEnum` to check the status of individual files.
   */
  const currentUploadStatus: QuickShareStatusEnum = useMemo(() => {
    let filesToCheck: GroupedUploadItem[] = [];

    if (uploadQuickShareGroupedFilesQueueWithoutRootFolder.length > 0) {
      filesToCheck = uploadQuickShareGroupedFilesQueueWithoutRootFolder;
    } else if (uploadQuickShareGroupedFilesQueue.length > 0) {
      filesToCheck = uploadQuickShareGroupedFilesQueue;
    }

    // Offline with an active upload queue: keep uploading UI (auto-resume when online)
    if (!isNetworkAvailable) {
      if (isUploadingQuickShareFiles) {
        return QuickShareStatusEnum.Uploading;
      }
      return QuickShareStatusEnum.ConnectionError;
    }

    // If we don't have files in the queue, the status is "Pending"
    if (filesToCheck.length === 0) {
      return QuickShareStatusEnum.Pending;
    }

    // Check if some files have FileUploadStatusEnum.FailedSharing
    const someFailedSharing = filesToCheck.some(
      (file) => file.status === FileUploadStatusEnum.FailedSharing,
    );
    if (someFailedSharing) {
      return QuickShareStatusEnum.ShareError;
    }

    // Verify if all files are completed
    const allCompleted = filesToCheck.every(
      (file) => file.status === FileUploadStatusEnum.Completed,
    );
    if (allCompleted) {
      return QuickShareStatusEnum.Completed;
    }

    // 4. Verify if all files are failed
    if (
      !isUploadingQuickShareFiles &&
      uploadedQuickShareGroupedFilesAmount === 0 &&
      totalQuickShareGroupedFilesAmount > 0
    ) {
      return QuickShareStatusEnum.Error;
    }

    // Verify if there is at least one file failed (but not all)
    if (
      !isUploadingQuickShareFiles &&
      uploadedQuickShareGroupedFilesAmount > 0 &&
      uploadedQuickShareGroupedFilesAmount < totalQuickShareGroupedFilesAmount
    ) {
      return QuickShareStatusEnum.SomeErrors;
    }

    // If there are no files all-completed, nor all-failed, and you haven't lost the connection, you are still uploading
    return QuickShareStatusEnum.Uploading;
  }, [
    isNetworkAvailable,
    isUploadingQuickShareFiles,
    uploadQuickShareGroupedFilesQueue,
    uploadQuickShareGroupedFilesQueueWithoutRootFolder,
    uploadedQuickShareGroupedFilesAmount,
    totalQuickShareGroupedFilesAmount,
  ]);

  const areNewUsersOrGroupsSelected = useMemo(() => {
    return quickShareForm.users.length > 0 || quickShareForm.groups.length > 0;
  }, [quickShareForm.users, quickShareForm.groups]);

  /**
   * A function to handle the uploading of files by initializing a root folder with pre-defined attributes
   * and invoking the upload process. The function creates a new root folder object, assigns it with a unique
   * identifier, predefined attributes, and a queue of files (children), and passes it to the upload process.
   *
   * @function handleUploadFiles
   * @returns {void} Does not return any value.
   */
  const handleSubmit = (): void => {
    if (currentUploadStatus === QuickShareStatusEnum.Completed) {
      emptyQuickShareUploadFilesQueue();
      setFilesQueue([]);
      setGroupedFilesQueue([]);
      //currentFolderName = `Quick Share ${new Date().toLocaleString()}`;
      return;
    }

    fileService
      .createFolder({
        name: SpecialFolderEnum.QuickShare,
        parent: undefined,
      })
      .then((folder) => {
        const children = buildFileTreeFromUploadFiles(filesQueue) ?? [];

        // Serve per “root” folder finta solo se serve
        function setParentUidRecursive(
          nodes: UploadFile[],
          parentUid: number | null,
          parentUUID: string | null,
        ) {
          nodes.forEach((node) => {
            node.parentUid = parentUid;
            node.parentUUID = parentUUID;
            if (node.isFolder && node.children && node.children.length > 0) {
              setParentUidRecursive(node.children, null, node.id);
            }
          });
        }

        // Case 1: only one file

        if (children.length === 1 && children[0] && !children[0].isFolder) {
          children[0].parentUid = folder.id;
          children[0].parentUUID = null;
          uploadFiles(children, true, folder.id, undefined, folder.isPasswordSet, folder.fileName);
        }
        // Case 2: only one folder
        else if (children.length === 1 && children?.[0]?.isFolder) {
          // Upload diretto della cartella (e dei figli) nella cartella Quick Share
          setParentUidRecursive(children, folder.id, null);
          uploadFiles(children, true, folder.id, undefined, folder.isPasswordSet, folder.fileName);
        }
        // Case 3: more files/folders: create fake root
        else {
          const rootFolder: UploadFile = {
            id: generateUUID(),
            fileName: `Quick Share ${new Date().toLocaleString()}`,
            isFolder: true,
            file: undefined,
            progress: 0,
            status: FileUploadStatusEnum.Pending,
            isQuickShare: true,
            retries: 0,
            children: children,
            batch: generateUUID(),
          };

          // Set parentUid to the children of the root (NOT to the root itself)
          setParentUidRecursive(rootFolder.children!, null, rootFolder.id);

          // Root goes under the real Quick Share folder
          rootFolder.parentUid = folder.id;
          rootFolder.parentUUID = null;

          uploadFiles(
            [rootFolder],
            true,
            folder.id,
            undefined,
            folder.isPasswordSet,
            folder.fileName,
          );
        }

        setFilesQueue([]);
        setGroupedFilesQueue([]);
      })
      .catch(() => {
        notification.error({
          title: t('errors.hitASnag'),
        });
      });
  };

  /**
   * Removes a specified file from the grouped files queue.
   *
   * @function handleRemoveFile
   * @param {GroupedUploadItem} item - The file item to be removed from the queue.
   */
  const handleRemoveFile = (item: GroupedUploadItem) => {
    // Remove the file from the grouped files queue
    setGroupedFilesQueue((prevGroupedFilesQueue) =>
      prevGroupedFilesQueue.filter((file) => file.id !== item.id),
    );

    setFilesQueue((prevFilesQueue) => prevFilesQueue.filter((file) => file.id !== item.id));
  };

  /**
   * Handles the upload of files by processing them and updating the state.
   *
   * @param {FileList | DataTransferItemList} files - A list of files or data transfer items to upload.
   *
   * This function performs the following steps:
   * 1. Pushes the provided files to the state through `setFilesToUpload`.
   * 2. Retrieves the file tree structure using `getFileTree`.
   * 3. Determines the files to enqueue for upload using `getFilesToEnqueue`.
   * 4. Updates the upload queue state with the file enqueue response using `setFilesQueue`.
   * 5. Groups the files to enqueue and updates the grouped files queue state using `setGroupedFilesQueue`.
   * 6. Outputs the grouped files to enqueue into the console for debugging purposes.
   */
  const handleAddFiles = (files: FileList | DataTransferItemList) => {
    getFileTree(files).then((fileTree) => {
      const filesToEnqueueResponse = getFilesToEnqueue(fileTree);

      setFilesQueue((prevFilesQueue) => [...prevFilesQueue, ...filesToEnqueueResponse]);

      const groupedFilesToEnqueueResponse = groupUploadItems(filesToEnqueueResponse, true);

      setGroupedFilesQueue((prevGroupedFilesQueue) => [
        ...prevGroupedFilesQueue,
        ...groupedFilesToEnqueueResponse,
      ]);
    });
  };

  /**
   * A function that handles the cancellation of an action or process.
   * When invoked, it clears the queue of files that were pending for
   * quick sharing by calling `emptyQuickShareUploadFilesQueue`.
   */
  const handleCancel = () => {
    emptyQuickShareUploadFilesQueue();
    setFilesQueue([]);
    setGroupedFilesQueue([]);
    //currentFolderName = `Quick Share ${new Date().toLocaleString()}`;
  };

  /**
   * A memoized variable that determines the files to be used based on the current state of file queues.
   * If the `uploadQuickShareGroupedFilesQueue` contains files, those files are returned.
   * Otherwise, it defaults to using `groupedFilesQueue`.
   *
   * Dependencies:
   * - `groupedFilesQueue`: The default queue of grouped files.
   * - `uploadQuickShareGroupedFilesQueue`: The queue of grouped files specifically for quick sharing uploads.
   */
  const files = useMemo(() => {
    if (groupedFilesQueue.length === 1 && uploadQuickShareGroupedFilesQueue.length > 0) {
      return uploadQuickShareGroupedFilesQueue;
    } else if (uploadQuickShareGroupedFilesQueueWithoutRootFolder.length > 0) {
      return uploadQuickShareGroupedFilesQueueWithoutRootFolder;
    } else {
      return groupedFilesQueue;
    }
  }, [
    groupedFilesQueue,
    uploadQuickShareGroupedFilesQueue,
    uploadQuickShareGroupedFilesQueueWithoutRootFolder,
  ]);

  /**
   * Determines if the submit button should be disabled based on the current state
   * of files, users, and groups.
   *
   * The button is disabled when:
   * - There are no files and no users, or
   * - There are no groups.
   *
   * Dependencies:
   * - `files`: An array representing the files associated with the quickShareForm.
   * - `quickShareForm.users`: An array representing the users involved in the quickShareForm.
   * - `quickShareForm.groups`: An array representing the groups involved in the quickShareForm.
   */
  const isSubmitButtonDisabled = useMemo(() => {
    if (currentUploadStatus === QuickShareStatusEnum.Completed) {
      return false;
    }

    if (
      [
        QuickShareStatusEnum.ConnectionError,
        QuickShareStatusEnum.Error,
        QuickShareStatusEnum.ShareError,
      ].includes(currentUploadStatus)
    ) {
      return true;
    }

    return (
      files.length === 0 ||
      (quickShareForm.users.length === 0 && quickShareForm.groups.length === 0)
    );
  }, [files, quickShareForm.users, quickShareForm.groups, currentUploadStatus]);

  /**
   * A derived state that determines the visibility of the "Cancel" button
   * based on the current upload status. The value is computed using React's
   * `useMemo` for performance optimization*/
  const isCancelButtonVisible = useMemo(() => {
    switch (currentUploadStatus) {
      case QuickShareStatusEnum.SomeErrors:
      case QuickShareStatusEnum.ShareError:
      case QuickShareStatusEnum.Error:
        return true;
      case QuickShareStatusEnum.Uploading:
        return true;

      default:
        return false;
    }
  }, [currentUploadStatus]);

  /**
   * Indicates whether the form is disabled based on the current state of the upload queue.
   * The form is considered disabled if there are files in the `uploadQuickShareGroupedFilesQueue`.
   * This value is memoized to avoid unnecessary re-evaluation.
   *
   * Dependencies:
   * - `uploadQuickShareGroupedFilesQueue`: An array representing the queue of grouped files awaiting upload.
   *
   * @type {boolean}
   */
  const isFormDisabled: boolean = useMemo(() => {
    return uploadQuickShareGroupedFilesQueue.length > 0;
  }, [uploadQuickShareGroupedFilesQueue]);

  /**
   * Represents a computed label for the submit button based on the current upload status.
   * The label dynamically changes to provide contextual information to the user during
   * different stages of the upload and share process.
   *
   * Dependencies:
   * - `currentUploadStatus`: The current status of the upload process,
   *   determined by `QuickShareStatusEnum`.
   * - `t`: Typically a translation function or language dependency.
   *
   * Possible values:
   * - "Start Upload and Share": When the status is `Pending`, `ConnectionError`, or `Error`.
   * - "Processing...": When the status is `Uploading`.
   * - "Start new Upload and Share": When the status is `Completed`.
   * - "Continue": When the status is `SomeErrors`.
   */
  const submitButtonLabel = useMemo(() => {
    switch (currentUploadStatus) {
      case QuickShareStatusEnum.Pending:
      case QuickShareStatusEnum.ConnectionError:
      case QuickShareStatusEnum.Error:
      case QuickShareStatusEnum.ShareError:
        return t('common.startUploadAndShare');
      case QuickShareStatusEnum.Uploading:
        return `${t('common.processing')}...`;
      case QuickShareStatusEnum.Completed:
        return t('common.startNewUploadAndShare');
      case QuickShareStatusEnum.SomeErrors:
        return t('common.continue');
    }
  }, [currentUploadStatus, t]);

  /**
   * Returns the list of files to be displayed in the Quick Share component.
   *
   * If the upload is in progress, it returns the files from the `uploadQuickShareGroupedFilesQueueWithoutRootFolder`
   * if there are more than one file, otherwise it returns the files from the `uploadQuickShareGroupedFilesQueue`.
   *
   * If the upload is not in progress, it returns the files from the `files` array.
   *
   */
  const uploadFilesList = useMemo(() => {
    if (isUploadingQuickShareFiles) {
      if (uploadQuickShareGroupedFilesQueueWithoutRootFolder.length > 1) {
        return uploadQuickShareGroupedFilesQueueWithoutRootFolder;
      } else {
        return uploadQuickShareGroupedFilesQueue;
      }
    } else {
      return files;
    }
  }, [
    isUploadingQuickShareFiles,
    uploadQuickShareGroupedFilesQueue,
    uploadQuickShareGroupedFilesQueueWithoutRootFolder,
    files,
  ]);

  useEffect(() => {
    setIsPageLoaded(true);
  }, []);

  useEffect(() => {
    // Check if user is External or Reader and redirect to /
    if (isExternal || isReader) {
      //redirectLoggedInUser();
      return;
    }
  }, [isExternal, isReader]);

  // Do not redirect from other routes (e.g. /settings, /files/shared) since useQuickShare is also used by useHeader.
  useEffect(() => {
    const isOnQuickShareRoute = location.pathname.startsWith('/quick-share');
    if (isOnQuickShareRoute && authenticatedUser && authenticatedUser.isQSAvailable === false) {
      redirectLoggedInUser();
    }
  }, [location.pathname, authenticatedUser, redirectLoggedInUser]);

  return {
    t,
    handleAddFiles,
    handleSubmit,
    handleRemoveFile,
    isUploadingQuickShareFiles,
    handleQuickShareInputChange,
    quickShareForm,
    files,
    isSubmitButtonDisabled,
    uploadQuickShareGroupedFilesQueue,
    currentUploadStatus,
    isFormDisabled,
    submitButtonLabel,
    isCancelButtonVisible,
    handleCancel,
    failedUploadQuickShareFiles,
    retryFailedUploads: () => retryFailedUploadsFromContext(true),
    handleShareQuickShareFiles,
    isPageLoaded,
    areNewUsersOrGroupsSelected,
    quickShareGlobalProgressByBytes,
    uploadQuickShareGroupedFilesQueueWithoutRootFolder,
    uploadFilesList,
    isNetworkAvailable,
  };
}

export default useQuickShare;
